import axiosInstance, { endpoints } from 'src/utils/axios';

// ----------------------------------------------------------------------
// Open Play rotation: Ready pool → Smart / Manual rotation → match → result.
// Every call unwraps the backend envelope and returns `data`.
// ----------------------------------------------------------------------

const unwrap = (response, fallbackMessage) => {
  if (response.data?.status !== 'success') {
    throw new Error(response.data?.message || fallbackMessage);
  }
  return response.data.data;
};

/**
 * One id per click. The server remembers it, so a double click, a retry or a
 * second tab replaying the same action gets the first answer back instead of
 * a second lineup or a second rotation.
 */
export function newRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

const withRequestId = (requestId) => (requestId ? { request_id: requestId } : undefined);

export const openPlayApi = {
  /** Backend-owned view of what a rotation can do right now (eligible players, courts, blockers). */
  async getRotationState(sessionId) {
    const response = await axiosInstance.get(endpoints.openPlay.rotationState(sessionId));
    return unwrap(response, 'Failed to load rotation state');
  },

  /** Smart AI Rotation: the server picks and assigns lineups, then notifies players. */
  async startRotation(sessionId, { requestId } = {}) {
    const response = await axiosInstance.post(
      endpoints.openPlay.startRotation(sessionId),
      withRequestId(requestId)
    );
    return unwrap(response, 'Failed to start rotation');
  },

  /** Smart AI Rotation "New Queue": the server picks the next four Ready players as a court-less lineup. */
  async createSmartQueue(sessionId, { requestId } = {}) {
    const response = await axiosInstance.post(
      endpoints.openPlay.createSmartQueue(sessionId),
      withRequestId(requestId)
    );
    return unwrap(response, 'Failed to create queue');
  },

  /**
   * Manual "New Queue": an empty lineup to fill in, or a full 2v2 in one call.
   * Validated on the server like every other assignment.
   */
  async createQueue(sessionId, { homePlayerIds, awayPlayerIds, requestId } = {}) {
    const response = await axiosInstance.post(endpoints.openPlay.createQueue(sessionId), {
      ...(homePlayerIds?.length ? { home_player_ids: homePlayerIds } : {}),
      ...(awayPlayerIds?.length ? { away_player_ids: awayPlayerIds } : {}),
      ...withRequestId(requestId),
    });
    return unwrap(response, 'Failed to create queue');
  },

  /** Replace a queued lineup's four players in one step. */
  async setQueuePlayers(sessionId, matchId, { homePlayerIds, awayPlayerIds }) {
    const response = await axiosInstance.put(
      endpoints.openPlay.setQueuePlayers(sessionId, matchId),
      { home_player_ids: homePlayerIds, away_player_ids: awayPlayerIds }
    );
    return unwrap(response, 'Failed to update the lineup');
  },

  /** Put a queued lineup on a free court; the server checks the court and the players. */
  async assignQueueToCourt(sessionId, matchId, courtId) {
    const response = await axiosInstance.post(endpoints.openPlay.assignQueue(sessionId, matchId), {
      court_id: courtId,
    });
    return unwrap(response, 'Failed to assign the lineup');
  },

  /**
   * Take a match off the board without a result: a match in play, winners
   * holding a court, or a queued lineup. Players go back to Ready.
   */
  async removeMatch(sessionId, matchId) {
    const response = await axiosInstance.delete(endpoints.openPlay.removeMatch(sessionId, matchId));
    return unwrap(response, 'Failed to remove the match');
  },

  /** Free a court that a finished session left a live match on. */
  async clearCourt(sessionId, courtId) {
    const response = await axiosInstance.post(endpoints.openPlay.clearCourt(sessionId, courtId));
    return unwrap(response, 'Failed to clear the court');
  },

  /** Manual Queuing: the admin's 2v2 on a court (or filling a court held by winners). */
  async createMatch(sessionId, { courtId, homePlayerIds, awayPlayerIds, fillMatchId }) {
    const response = await axiosInstance.post(endpoints.openPlay.createMatch(sessionId), {
      court_id: courtId,
      home_player_ids: homePlayerIds,
      away_player_ids: awayPlayerIds,
      ...(fillMatchId ? { fill_match_id: fillMatchId } : {}),
    });
    return unwrap(response, 'Failed to create match');
  },

  /** Score + explicit winner; applies the session rules and (optionally) the next Smart pass. */
  async completeMatch(sessionId, matchId, { homeScore, awayScore, winnerSide, autoRotate }) {
    const response = await axiosInstance.post(
      endpoints.openPlay.completeMatch(sessionId, matchId),
      {
        home_score: homeScore,
        away_score: awayScore,
        winner_side: winnerSide,
        ...(autoRotate === undefined ? {} : { auto_rotate: autoRotate }),
      }
    );
    return unwrap(response, 'Failed to complete match');
  },

  /** Re-sends the "you're up" push to this match's players only. Throttled server-side. */
  async resendNotifications(sessionId, matchId) {
    const response = await axiosInstance.post(
      endpoints.openPlay.resendNotification(sessionId, matchId)
    );
    return unwrap(response, 'Failed to resend notifications');
  },

  /** Admin sets a player's availability (READY | ON_BREAK | NOT_READY) on their behalf. */
  async setPlayerAvailability(sessionId, userId, availability) {
    const response = await axiosInstance.patch(
      endpoints.openPlay.setAvailability(sessionId, userId),
      { availability }
    );
    return unwrap(response, 'Failed to update availability');
  },
};

const errorBody = (err) =>
  err?.data?.data && typeof err.data.data === 'object' ? err.data.data : err?.data;

/** Blockers the backend attached to a 409 (NO_ROTATION_POSSIBLE) or a plain message. */
export function rotationErrorLines(err) {
  const blockers = errorBody(err)?.blockers;
  if (Array.isArray(blockers) && blockers.length) return blockers;
  return [err?.message || 'Unable to start rotation'];
}

/** The server's stable code for a refusal (`MATCHES_IN_PLAY`, `SESSION_ENDED`, …), if any. */
export function openPlayErrorCode(err) {
  return errorBody(err)?.code ?? null;
}
